import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { ArrowRight, Shield, Leaf, Users, ChevronRight, Download, Play } from 'lucide-react';
import { Button } from '../components/ui/button';
import { usePageContent } from '../hooks/usePageContent';

const API = process.env.REACT_APP_BACKEND_URL;

const DEFAULTS = {
  hero_label: 'Fabricant Français depuis 1957',
  hero_title: 'Nous participons à la construction',
  hero_title_accent: "d'ouvrages techniques et durables.",
  hero_subtitle: "Spécialisé dans la pré-fabrication d'éléments structurels en béton, le groupe SOPREL met son expertise au service des acteurs du secteur du BTP.",
  hero_video: '/hero_soprel.mp4',
  hero_bg_image: 'https://static.prod-images.emergentagent.com/jobs/d59d775d-5692-4c00-811c-246ab4499e3b/images/bb772e928b0a24500ef8797bceac569ca367a873549bf837f33b9ec01aa31b05.png',
  intro_label: 'Qui sommes-nous',
  intro_title: 'Groupe SOPREL, innovation et performance',
  intro_text: "SOPREL se distingue également par sa capacité à répondre aux besoins spécifiques de ses clients, en offrant des solutions sur mesure, tout en mettant en avant des solutions environnementales et les exigences de sécurité les plus strictes.",
  intro_image1: 'https://customer-assets.emergentagent.com/job_soprel-build/artifacts/f3bk8o7c_SOPREL_V1_27600_0055.JPG',
  intro_image2: 'https://customer-assets.emergentagent.com/job_soprel-build/artifacts/7gwwkr0a_esternay.png',
  intro_image3: 'https://customer-assets.emergentagent.com/job_soprel-build/artifacts/p1rzcg7f_image001.png',
  pillar1_title: 'Innover',
  pillar1_text: 'Pour un futur décarboné en proposant des solutions techniques et industrielles performantes qui répondent aux défis de la construction durable.',
  pillar2_title: 'Préserver',
  pillar2_text: "L'environnement, c'est favoriser les circuits courts, utiliser des matériaux recyclés et réduire notre empreinte carbone.",
  pillar3_title: 'Accompagner',
  pillar3_text: "Nos clients depuis leurs choix techniques jusqu'à la livraison sur les chantiers. Conseil et réactivité font partie de l'ADN de l'entreprise.",
  cta_title: 'Un projet de construction ?',
  cta_text: 'Nos équipes sont à votre disposition pour étudier vos besoins et vous proposer les solutions les plus adaptées.',
};

const FALLBACK_IMAGE = 'https://static.prod-images.emergentagent.com/jobs/d59d775d-5692-4c00-811c-246ab4499e3b/images/762ee186b80256b2dde7991e0b620afe88364e42ed9db8e2b7650e715e94d87f.png';

const HeroSection = ({ c }) => {
  return (
    <section className="relative min-h-screen flex items-center justify-center overflow-hidden bg-[#0e1c3f]" data-testid="hero-section">
      <div className="absolute inset-0 w-full h-full">
        <video autoPlay loop muted playsInline className="absolute inset-0 w-full h-full object-cover" data-testid="hero-video">
          <source src={c.hero_video} type="video/mp4" />
        </video>
      </div>
      <div className="absolute inset-0 bg-gradient-to-b from-[#0e1c3f]/50 via-[#0e1c3f]/40 to-[#0e1c3f]/70" />
      <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
        <div>
          <p className="text-[#0078b7] font-body font-bold tracking-[0.2em] uppercase text-sm mb-6">{c.hero_label}</p>
          <h1 className="font-display font-bold text-3xl sm:text-5xl lg:text-7xl text-white uppercase tracking-tight leading-tight mb-6">
            {c.hero_title}{' '}
            <span className="text-[#ff65c3]">{c.hero_title_accent}</span>
          </h1>
          <p className="text-slate-300 text-base sm:text-lg lg:text-xl max-w-2xl mx-auto mb-8 sm:mb-10 leading-relaxed">{c.hero_subtitle}</p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button asChild size="lg" className="bg-[#0078b7] hover:bg-[#0078b7]/90 text-white font-medium px-8 rounded-sm" data-testid="hero-cta-products">
              <Link to="/produits">Découvrir nos produits<ArrowRight className="ml-2 h-5 w-5" /></Link>
            </Button>
            <Button asChild size="lg" variant="outline" className="border-white text-white hover:bg-white/10 rounded-sm" data-testid="hero-cta-contact">
              <Link to="/contact">Nous contacter</Link>
            </Button>
          </div>
        </div>
      </div>
      <div className="absolute bottom-8 left-1/2 -translate-x-1/2 animate-bounce">
        <div className="w-8 h-12 border-2 border-white/50 rounded-full flex items-start justify-center p-2">
          <div className="w-1.5 h-3 bg-white/80 rounded-full" />
        </div>
      </div>
    </section>
  );
};

const IntroSection = ({ c }) => {
  return (
    <section className="py-24 bg-white" data-testid="intro-section">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-16 items-center">
          <div>
            <p className="text-[#0078b7] font-bold tracking-[0.2em] uppercase text-sm mb-4">{c.intro_label}</p>
            <h2 className="font-display font-bold text-3xl sm:text-4xl text-[#0e1c3f] uppercase tracking-tight mb-6">{c.intro_title}</h2>
            <p className="text-slate-600 text-lg leading-relaxed mb-8">{c.intro_text}</p>
            <Button asChild className="bg-[#0e1c3f] hover:bg-[#0e1c3f]/90 text-white rounded-sm">
              <Link to="/groupe">En savoir plus<ChevronRight className="ml-2 h-4 w-4" /></Link>
            </Button>
          </div>
          <div className="relative">
            <div className="flex gap-3 items-start">
              <div className="flex-1 pt-12">
                <div className="aspect-[3/4] overflow-hidden rounded-sm shadow-xl group">
                  <img src={c.intro_image1} alt="Production SOPREL" className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" />
                </div>
              </div>
              <div className="w-[42%] flex flex-col gap-3">
                <div className="aspect-[3/4] overflow-hidden rounded-sm shadow-xl group">
                  <img src={c.intro_image2} alt="Production SOPREL - Précontrainte" className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" />
                </div>
                <div className="aspect-[3/4] overflow-hidden rounded-sm shadow-xl group">
                  <img src={c.intro_image3} alt="Production SOPREL - Armatures" className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" />
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

const PillarsSection = ({ c }) => {
  const pillars = [
    { icon: Leaf, title: c.pillar1_title, description: c.pillar1_text, color: 'cyan' },
    { icon: Shield, title: c.pillar2_title, description: c.pillar2_text, color: 'navy' },
    { icon: Users, title: c.pillar3_title, description: c.pillar3_text, color: 'magenta' },
  ];

  return (
    <section className="py-24 bg-slate-50" data-testid="pillars-section">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-16">
          <p className="text-[#0078b7] font-bold tracking-[0.2em] uppercase text-sm mb-4">Notre approche</p>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
          {pillars.map((pillar, index) => {
            const Icon = pillar.icon;
            return (
              <div key={pillar.title} className="bg-white p-8 border border-slate-200 hover:border-[#0078b7] transition-all hover:-translate-y-1 hover:shadow-lg group" style={{ animationDelay: `${index * 100}ms` }}>
                <div className={`w-14 h-14 rounded-sm flex items-center justify-center mb-6 transition-colors ${
                  pillar.color === 'cyan' ? 'bg-[#0078b7]/10 text-[#0078b7] group-hover:bg-[#0078b7] group-hover:text-white' :
                  pillar.color === 'navy' ? 'bg-[#0e1c3f]/10 text-[#0e1c3f] group-hover:bg-[#0e1c3f] group-hover:text-white' :
                  'bg-[#ff65c3]/10 text-[#ff65c3] group-hover:bg-[#ff65c3] group-hover:text-white'
                }`}>
                  <Icon className="h-7 w-7" />
                </div>
                <h3 className="font-heading font-bold text-xl text-[#0e1c3f] mb-4">{pillar.title}</h3>
                <p className="text-slate-600 leading-relaxed">{pillar.description}</p>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
};

const ProductsShowcase = () => {
  const [products, setProducts] = useState([]);
  const [activeProduct, setActiveProduct] = useState(0);
  const [isHovering, setIsHovering] = useState(false);

  useEffect(() => {
    const fetchProducts = async () => {
      try {
        const { data } = await axios.get(`${API}/api/products`);
        setProducts(data.slice(0, 5));
      } catch (e) { console.error('Error fetching products:', e); }
    };
    fetchProducts();
  }, []);

  useEffect(() => {
    if (isHovering || products.length === 0) return;
    const interval = setInterval(() => {
      setActiveProduct((prev) => (prev + 1) % products.length);
    }, 4000);
    return () => clearInterval(interval);
  }, [products.length, isHovering]);

  const currentProduct = products[activeProduct];

  if (products.length === 0) return null;

  return (
    <section className="relative bg-[#0e1c3f] overflow-hidden" data-testid="products-showcase">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 sm:py-24">
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-end gap-6 mb-8 sm:mb-12">
          <div>
            <p className="text-[#0078b7] font-bold tracking-[0.2em] uppercase text-sm mb-4">Nos solutions</p>
            <h2 className="font-display font-bold text-3xl sm:text-4xl text-white uppercase tracking-tight">Des produits de haute performance</h2>
          </div>
        </div>

        <div className="relative overflow-hidden" onMouseEnter={() => setIsHovering(true)} onMouseLeave={() => setIsHovering(false)}>
          {products.map((product, index) => (
            <div key={product.id} className={`transition-all duration-700 ${index === activeProduct ? 'relative opacity-100 translate-x-0' : 'absolute inset-0 opacity-0 translate-x-8 pointer-events-none'}`}>
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 items-center">
                <div className="relative aspect-[4/3] overflow-hidden bg-[#0e1c3f]">
                  {product.image_url && <img src={product.image_url} alt={product.name} className="w-full h-full object-cover opacity-80 group-hover:opacity-100 transition-opacity" />}
                  <div className="absolute inset-0 bg-gradient-to-r from-transparent to-[#0e1c3f]/80" />
                </div>
                <div className="py-4 sm:py-8">
                  <span className="inline-block px-4 py-1.5 bg-[#0078b7]/20 text-[#0078b7] text-xs font-bold uppercase tracking-wider mb-4 sm:mb-6">{String(activeProduct + 1).padStart(2, '0')} / {String(products.length).padStart(2, '0')}</span>
                  <h2 className="font-display font-bold text-2xl sm:text-3xl lg:text-4xl xl:text-5xl text-white uppercase mb-3 sm:mb-6 tracking-tight">{currentProduct.name}</h2>
                  <p className="text-slate-300 text-sm sm:text-base lg:text-lg mb-4 sm:mb-6 leading-relaxed line-clamp-3 sm:line-clamp-none">{product.short_description}</p>
                  <div className="hidden sm:flex flex-wrap gap-3 mb-8">
                    {product.features?.slice(0, 3).map((feature, i) => (
                      <span key={i} className="px-4 py-2 bg-white/10 backdrop-blur-sm text-white text-sm rounded-sm border border-white/20">{feature}</span>
                    ))}
                  </div>
                  <div className="flex gap-4">
                    <Button asChild className="bg-[#0078b7] hover:bg-[#0078b7]/90 text-white rounded-sm">
                      <Link to={`/produits/${product.slug}`}>Découvrir<ChevronRight className="ml-2 h-4 w-4" /></Link>
                    </Button>
                    {product.pdf_url && (
                      <Button asChild variant="outline" className="border-[#ff65c3] text-[#ff65c3] hover:bg-[#ff65c3]/10 rounded-sm">
                        <a href={product.pdf_url} target="_blank" rel="noopener noreferrer"><Download className="mr-2 h-4 w-4" />Fiche PDF</a>
                      </Button>
                    )}
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>

        <div className="mt-4 sm:mt-8 grid grid-cols-2 sm:grid-cols-5 gap-2 sm:gap-4">
          {products.map((product, index) => (
            <button key={product.id} onClick={() => setActiveProduct(index)} className={`relative p-4 text-left transition-all duration-300 group ${index === activeProduct ? 'bg-[#0078b7]' : 'bg-white/5 hover:bg-white/10'}`} data-testid={`product-nav-${product.slug}`}>
              {index === activeProduct && !isHovering && <div className="absolute bottom-0 left-0 h-1 bg-[#ff65c3] animate-progress" />}
              <span className={`block font-heading font-bold text-sm mb-1 ${index === activeProduct ? 'text-white' : 'text-[#0078b7]'}`}>{String(index + 1).padStart(2, '0')}</span>
              <span className={`block font-medium text-sm ${index === activeProduct ? 'text-white' : 'text-white/70'}`}>{product.name}</span>
            </button>
          ))}
        </div>
      </div>
    </section>
  );
};

const RealisationsPreview = () => {
  const [realisations, setRealisations] = useState([]);

  useEffect(() => {
    const fetchRealisations = async () => {
      try {
        const { data } = await axios.get(`${API}/api/realisations`);
        setRealisations(data.filter(r => r.is_featured).slice(0, 3));
      } catch (e) { console.error('Error fetching realisations:', e); }
    };
    fetchRealisations();
  }, []);

  return (
    <section className="py-24 bg-white" data-testid="realisations-preview-section">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-end gap-6 mb-12">
          <div>
            <p className="text-[#0078b7] font-bold tracking-[0.2em] uppercase text-sm mb-4">Nos références</p>
            <h2 className="font-display font-bold text-3xl sm:text-4xl text-[#0e1c3f] uppercase tracking-tight">Nos réalisations historiques</h2>
          </div>
          <Button asChild variant="outline" className="border-[#0e1c3f] text-[#0e1c3f] hover:bg-[#0e1c3f]/5 rounded-sm">
            <Link to="/realisations">Toutes les réalisations<ArrowRight className="ml-2 h-4 w-4" /></Link>
          </Button>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {realisations.length > 0 ? realisations.map((realisation, index) => (
            <div key={realisation.id} className={`group relative overflow-hidden ${index === 0 ? 'md:col-span-2 md:row-span-2' : ''}`} style={{ animationDelay: `${index * 100}ms` }}>
              <div className={`${index === 0 ? 'aspect-[16/10]' : 'aspect-[4/3]'} overflow-hidden`}>
                <img src={realisation.image_url || FALLBACK_IMAGE} alt={realisation.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" />
              </div>
              <div className="absolute inset-0 bg-gradient-to-t from-[#0e1c3f] via-[#0e1c3f]/40 to-transparent opacity-80 group-hover:opacity-90 transition-opacity" />
              <div className="absolute bottom-0 left-0 right-0 p-6">
                <div className="flex items-center gap-2 mb-2">
                  {realisation.year && <span className="text-[#0078b7] text-xs font-bold">{realisation.year}</span>}
                  {realisation.location && <span className="text-white/60 text-xs">• {realisation.location}</span>}
                </div>
                <h3 className="font-heading font-bold text-xl text-white mb-2 group-hover:text-[#0078b7] transition-colors">{realisation.title}</h3>
                <p className="text-white/70 text-sm line-clamp-2">{realisation.description}</p>
              </div>
            </div>
          )) : (
            <div className="col-span-3 text-center py-12"><p className="text-slate-400">Découvrez bientôt nos réalisations</p></div>
          )}
        </div>
      </div>
    </section>
  );
};

const CTASection = ({ c }) => {
  return (
    <section className="py-24 bg-[#0078b7]" data-testid="cta-section">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
        <h2 className="font-display font-bold text-3xl sm:text-4xl text-white uppercase tracking-tight mb-6">{c.cta_title}</h2>
        <p className="text-white/80 text-lg mb-10 max-w-2xl mx-auto">{c.cta_text}</p>
        <div className="flex flex-col sm:flex-row gap-4 justify-center">
          <Button asChild size="lg" className="bg-white text-[#0078b7] hover:bg-slate-100 font-medium px-8 rounded-sm" data-testid="cta-contact-btn">
            <Link to="/contact">Demander un devis<ArrowRight className="ml-2 h-5 w-5" /></Link>
          </Button>
          <Button asChild size="lg" variant="outline" className="border-white text-white hover:bg-white/10 rounded-sm">
            <Link to="/produits">Consulter nos produits</Link>
          </Button>
        </div>
      </div>
    </section>
  );
};

const HomePage = () => {
  const { content: c } = usePageContent('home', DEFAULTS);

  return (
    <div data-testid="home-page">
      <HeroSection c={c} />
      <IntroSection c={c} />
      <PillarsSection c={c} />
      <ProductsShowcase />
      <RealisationsPreview />
      <CTASection c={c} />
    </div>
  );
};

export default HomePage;
